"use client";

import { useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

/**
 * Three states in one small page rather than three routes, since they're a
 * strict sequence for the same person in the same sitting:
 *   credentials -> (enroll, first login only) -> totp -> in
 */
type Step =
  | { kind: "credentials" }
  | { kind: "enroll"; username: string; secret: string; qrDataUrl: string }
  | { kind: "totp"; username: string };

export default function LoginPage() {
  const router = useRouter();
  const params = useSearchParams();
  const next = params.get("next") || "/";

  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Browser autofill (Edge, Chrome) fills the fields without firing React's
  // onChange until the person interacts with the page, so the state above can
  // read empty while the boxes visibly hold a username and password -- the
  // Continue button then stayed disabled. Read the fields themselves on submit.
  const userRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);

  const submitCredentials = async () => {
    const u = (userRef.current?.value ?? username).trim();
    const pw = passRef.current?.value ?? password;
    if (!u || !pw) { setErr("Enter your username and password."); return; }
    setErr(null); setBusy(true);
    try {
      const r = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ username: u, password: pw }),
      });
      const data = await r.json();
      if (!r.ok) { setErr(data.error ?? "Could not sign in."); return; }
      if (data.step === "done") {
        // This browser was already trusted — no TOTP step needed.
        router.push(next);
        router.refresh();
        return;
      }
      setStep(
        data.step === "enroll"
          ? { kind: "enroll", username: data.username, secret: data.secret, qrDataUrl: data.qrDataUrl }
          : { kind: "totp", username: data.username }
      );
    } finally { setBusy(false); }
  };

  const submitCode = async (secret?: string) => {
    setErr(null); setBusy(true);
    try {
      const uname = step.kind === "enroll" || step.kind === "totp" ? step.username : username;
      const r = await fetch("/api/auth/totp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ username: uname, code, secret, trustDevice }),
      });
      const data = await r.json();
      if (!r.ok) { setErr(data.error ?? "Could not verify that code."); return; }
      router.push(next);
      router.refresh();
    } finally { setBusy(false); }
  };

  const trustCheckbox = (
    <label className="flex items-center gap-2 text-[13px] text-slate-600">
      <input
        type="checkbox"
        checked={trustDevice}
        onChange={(e) => setTrustDevice(e.target.checked)}
        className="h-3.5 w-3.5 rounded border-slate-300"
      />
      Trust this browser for 30 days
    </label>
  );

  return (
    <div className="grid h-full place-items-center bg-slate-50">
      <div className="w-[340px] rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="mb-1 text-lg font-semibold text-slate-900">Go!Foton</h1>
        <p className="mb-5 text-sm text-slate-500">Cable Estimating</p>

        {step.kind === "credentials" && (
          <form
            className="space-y-3"
            onSubmit={(e) => { e.preventDefault(); submitCredentials(); }}
          >
            <input
              ref={userRef}
              autoFocus
              name="username"
              autoComplete="username"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
            <input
              ref={passRef}
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
            {err && <p className="text-[13px] text-red-600">{err}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-md bg-blue-600 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {busy ? "Checking…" : "Continue"}
            </button>
          </form>
        )}

        {step.kind === "enroll" && (
          <div className="space-y-3">
            <p className="text-[13px] text-slate-600">
              First login — scan this with an authenticator app (Google
              Authenticator, Microsoft Authenticator, Authy, 1Password, …),
              then enter the 6-digit code it shows.
            </p>
            <img src={step.qrDataUrl} alt="TOTP enrollment QR code" className="mx-auto h-40 w-40" />
            <input
              autoFocus
              placeholder="6-digit code"
              value={code}
              inputMode="numeric"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && submitCode(step.secret)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-center text-lg tracking-[0.3em] outline-none focus:border-blue-500"
            />
            {trustCheckbox}
            {err && <p className="text-[13px] text-red-600">{err}</p>}
            <button
              onClick={() => submitCode(step.secret)}
              disabled={busy || code.length !== 6}
              className="w-full rounded-md bg-blue-600 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {busy ? "Verifying…" : "Verify & finish setup"}
            </button>
          </div>
        )}

        {step.kind === "totp" && (
          <div className="space-y-3">
            <p className="text-[13px] text-slate-600">Enter the 6-digit code from your authenticator app.</p>
            <input
              autoFocus
              placeholder="6-digit code"
              value={code}
              inputMode="numeric"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && submitCode()}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-center text-lg tracking-[0.3em] outline-none focus:border-blue-500"
            />
            {trustCheckbox}
            {err && <p className="text-[13px] text-red-600">{err}</p>}
            <button
              onClick={() => submitCode()}
              disabled={busy || code.length !== 6}
              className="w-full rounded-md bg-blue-600 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {busy ? "Verifying…" : "Sign in"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
